import { appSettings } from 'AppSettings';
import useAuth from 'auth/UseAuth';
import React, { useCallback } from 'react';
import { PatientCountType } from '../components/grid/GridCountType';
import { Typography, useTheme } from '@mui/material';
import { BaseDownloadFile } from 'data/BaseData';
import { ValidatePatientCountParameters } from './PatientRecordValidation';
import { ItemOfResearchType } from 'data/ResearchProjectPatientData';
import { TimeInterval } from 'data/PatientRecordOptions';
import { DownloadButton } from '../components/DownloadButton';

export interface PatientRecordExportButtonProps {
  itemOfResearchId?: string;
  itemOfResearchType: ItemOfResearchType;
  timeInterval: TimeInterval;
  biobankId?: string;
  labId?: string;
  sampleTypeId?: string;
  numRecords: number;
  decrypted: boolean;
  countType: PatientCountType;
  disabled: boolean;
}

export const PatientRecordExportButton = ({
  itemOfResearchId,
  itemOfResearchType,
  timeInterval,
  biobankId,
  labId,
  sampleTypeId,
  countType,
  disabled,
}: PatientRecordExportButtonProps) => {
  ValidatePatientCountParameters(countType, biobankId, labId, sampleTypeId);

  const { accessToken } = useAuth();
  const theme = useTheme();

  const downloadData = useCallback(async () => {
    let url = `${appSettings.api.endpoint}/api/`;
    let params = itemOfResearchType === 'PathogenGroup' ? `?timeInterval=${timeInterval}` : '';

    url +=
      itemOfResearchType === 'DiseaseArea'
        ? `v2/DiseaseAreas/${itemOfResearchId}/PatientRecords`
        : `v2/PathogenGroups/${itemOfResearchId}/PatientRecords`;

    switch (countType) {
      case 'all':
        url += '/ExcelExport';
        break;
      case 'byBiobank':
        url += `/ByBiobank/biobank/${biobankId}/ExcelExport`;
        break;
      case 'byLab':
        url += `/ByLab/lab/${labId}/ExcelExport`;
        break;
      case 'bySampleType':
        url += `/BySampleType/sampleType/${sampleTypeId}/ExcelExport`;
        break;
    }

    await BaseDownloadFile(url + params, accessToken);
  }, [accessToken, biobankId, countType, itemOfResearchId, itemOfResearchType, labId, sampleTypeId, timeInterval]);

  return (
    <DownloadButton
      id='patient-record-export-button'
      downloadData={downloadData}
      disabled={disabled}
      sx={{
        color: theme.colors.alpha.black[100],
        ml: 5,
      }}
    >
      <Typography variant='button' ml={1}>
        Export
      </Typography>
    </DownloadButton>
  );
};
